import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Upload, FileText, Sparkles, CheckCircle, AlertCircle, Users, Euro, Leaf } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const TYPE_BADGE = {
  eta_prestations: { label: 'ETA Prestations', cls: 'bg-emerald-100 text-emerald-800' },
  tripartite_fourniture: { label: 'Tripartite – Fourniture', cls: 'bg-purple-100 text-purple-800' },
  fournisseur: { label: 'Fournisseur', cls: 'bg-amber-100 text-amber-800' },
  autre: { label: 'Autre', cls: 'bg-slate-100 text-slate-700' },
};

export default function ContratsIAUploadPage() {
  const [contrats, setContrats] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [selectedSociete, setSelectedSociete] = useState('');
  const [file, setFile] = useState(null);
  const [clientHint, setClientHint] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ client_nom: '', type_activite: '', surface_ha: '', prestations_souhaitees: '' });
  const [genResult, setGenResult] = useState(null);
  const [generating, setGenerating] = useState(false);

  const loadContrats = useCallback(async () => {
    try {
      const url = selectedSociete
        ? `/contrats-eta/?societe_id=${selectedSociete}`
        : '/contrats-eta/';
      const r = await api.get(url);
      setContrats(r.data.contrats || []);
    } catch (e) { console.error(e); }
  }, [selectedSociete]);

  useEffect(() => {
    (async () => {
      try {
        // Essai #1 : endpoint robuste qui ne dépend pas de Google
        let items = [];
        try {
          const r = await api.get('/google/all-societes-status');
          items = r.data?.societes || [];
        } catch { /* ignore */ }
        // Fallback : /tenant/societes (toujours dispo)
        if (!items.length) {
          try {
            const r2 = await api.get('/tenant/societes');
            items = r2.data?.societes || r2.data || [];
          } catch { /* ignore */ }
        }
        setSocietes(items);
        if (items.length && !selectedSociete) {
          setSelectedSociete(items[0].id || items[0].societe_id);
        }
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (selectedSociete) loadContrats(); }, [selectedSociete, loadContrats]);

  const handleUpload = async () => {
    if (!file || !selectedSociete) {
      toast.error('Sélectionne une société et un fichier');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('societe_id', selectedSociete);
    fd.append('fichier', file);
    if (clientHint) fd.append('client_nom', clientHint);
    try {
      const r = await api.post('/contrats-eta/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      toast.success(`Contrat analysé (${r.data.parse_method === 'vision' ? 'OCR vision' : 'texte'})`);
      setFile(null);
      setClientHint('');
      await loadContrats();
      setSelected(r.data.contrat);
    } catch (e) {
      toast.error('Erreur: ' + (e.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };

  const handleGenerer = async () => {
    if (!selectedSociete || !genForm.client_nom) {
      toast.error('Renseigne au moins le nom du client');
      return;
    }
    setGenerating(true);
    setGenResult(null);
    try {
      const r = await api.post('/contrats-eta/generer-nouveau', {
        societe_id: selectedSociete,
        client_nom: genForm.client_nom,
        type_activite: genForm.type_activite,
        surface_ha: genForm.surface_ha ? parseFloat(genForm.surface_ha) : null,
        prestations_souhaitees: genForm.prestations_souhaitees
          ? genForm.prestations_souhaitees.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      });
      if (r.data.success) {
        setGenResult(r.data);
        toast.success('Projet de contrat généré par IA');
      } else {
        toast.error(r.data.error || 'Aucun contrat de référence');
      }
    } catch (e) {
      toast.error('Erreur génération: ' + (e.response?.data?.detail || e.message));
    } finally { setGenerating(false); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="contrats-ia-upload-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-emerald-900 flex items-center gap-2">
          <Sparkles className="w-7 h-7" /> Cerveau Contrats IA
        </h1>
        <p className="text-slate-600 mt-1">
          Upload un contrat PDF/Word, l'IA extrait les prestations, tarifs et clauses. Ces prix deviennent prioritaires
          pour la facturation automatique des interventions chez ce client.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload + génération */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Uploader un contrat</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Société (workspace)</label>
                <select
                  data-testid="societe-select"
                  value={selectedSociete}
                  onChange={e => setSelectedSociete(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded"
                >
                  {societes.map(s => (
                    <option key={s.id || s.societe_id} value={s.id || s.societe_id}>
                      {s.nom || s.societe_nom}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Nom du client (optionnel)</label>
                <Input value={clientHint} onChange={e => setClientHint(e.target.value)} placeholder="Ex : Henri Bourg" data-testid="client-hint" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Fichier PDF / DOCX</label>
                <Input type="file" accept=".pdf,.docx,.txt" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="contrat-file-input" />
                <p className="text-xs text-slate-500 mt-1">Supporte les PDFs texte et scannés (OCR vision GPT)</p>
              </div>
              <Button
                data-testid="upload-contrat-btn"
                onClick={handleUpload}
                disabled={uploading || !file}
                className="w-full bg-emerald-700 hover:bg-emerald-800"
              >
                {uploading ? 'Analyse IA en cours...' : 'Analyser avec IA'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5" /> Générer un nouveau contrat</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-slate-500">
                L'IA s'appuie sur tes contrats déjà uploadés comme templates.
              </p>
              <Input
                placeholder="Nom du nouveau client"
                value={genForm.client_nom}
                onChange={e => setGenForm({ ...genForm, client_nom: e.target.value })}
                data-testid="gen-client-nom"
              />
              <Input
                placeholder="Type activité (viticulture, PPAM...)"
                value={genForm.type_activite}
                onChange={e => setGenForm({ ...genForm, type_activite: e.target.value })}
              />
              <Input
                type="number"
                placeholder="Surface (ha)"
                value={genForm.surface_ha}
                onChange={e => setGenForm({ ...genForm, surface_ha: e.target.value })}
              />
              <Input
                placeholder="Prestations souhaitées (séparées par virgules)"
                value={genForm.prestations_souhaitees}
                onChange={e => setGenForm({ ...genForm, prestations_souhaitees: e.target.value })}
              />
              <Button onClick={handleGenerer} disabled={generating} className="w-full" data-testid="gen-contrat-btn">
                {generating ? 'Génération...' : 'Générer projet'}
              </Button>
              {genResult && (
                <div className="mt-3 p-3 bg-purple-50 rounded text-xs space-y-2">
                  <div className="font-semibold text-purple-900">Projet pour {genResult.projet_contrat?.client_nom}</div>
                  {genResult.projet_contrat?.prestations?.map((p, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{p.type} ({p.surface_contractuelle || 0} {p.unite})</span>
                      <span className="font-bold">{p.prix_ht}€ HT/{p.unite}</span>
                    </div>
                  ))}
                  {genResult.projet_contrat?.justification && (
                    <div className="text-purple-800 italic border-t pt-2">{genResult.projet_contrat.justification}</div>
                  )}
                  <div className="text-slate-500">Références: {(genResult.contrats_references_utilises || []).join(', ')}</div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Liste contrats */}
        <div className="lg:col-span-2 space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><FileText className="w-5 h-5" /> Contrats analysés ({contrats.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[500px] overflow-auto">
              {contrats.length === 0 && (
                <p className="text-slate-500 text-sm">Aucun contrat pour cette société. Uploade un premier contrat à gauche.</p>
              )}
              {contrats.map(c => {
                const typeBadge = TYPE_BADGE[c.type_contrat] || TYPE_BADGE.autre;
                return (
                  <div
                    key={c.id}
                    onClick={() => setSelected(c)}
                    className={`p-3 border rounded cursor-pointer hover:bg-slate-50 ${selected?.id === c.id ? 'border-emerald-500 bg-emerald-50' : ''}`}
                    data-testid={`contrat-item-${c.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold">{c.client_nom || c.client_entreprise || '(sans nom)'}</div>
                      <Badge className={typeBadge.cls}>{typeBadge.label}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                      <span>{c.fichier_nom}</span>
                      {c.parse_ok ? (
                        <span className="flex items-center gap-1 text-emerald-700"><CheckCircle className="w-3 h-3" /> Analysé</span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-600"><AlertCircle className="w-3 h-3" /> Partiel</span>
                      )}
                      <span>{(c.prestations?.length || 0)} prestation(s) · {(c.prix_vente?.length || 0)} flux</span>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {selected && (
            <Card>
              <CardHeader>
                <CardTitle>Détail : {selected.client_nom}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-slate-500">Type :</span> {selected.type_contrat}</div>
                  <div><span className="text-slate-500">Activité :</span> {selected.type_activite || '—'}</div>
                  <div><span className="text-slate-500">Début :</span> {selected.date_debut || '—'}</div>
                  <div><span className="text-slate-500">Fin :</span> {selected.date_fin || '—'}</div>
                  {selected.surface_totale_contractuelle ? (
                    <div><span className="text-slate-500">Surface :</span> {selected.surface_totale_contractuelle} ha</div>
                  ) : null}
                  {selected.produit_objet ? (
                    <div><span className="text-slate-500">Produit :</span> {selected.produit_objet}</div>
                  ) : null}
                </div>

                {selected.parties?.length > 0 && (
                  <div>
                    <div className="font-semibold flex items-center gap-1 mb-2"><Users className="w-4 h-4" /> Parties au contrat</div>
                    <div className="space-y-1">
                      {selected.parties.map((p, i) => (
                        <div key={i} className="p-2 bg-slate-50 rounded">
                          <div className="text-xs uppercase text-slate-500">{p.role}</div>
                          <div className="font-semibold">{p.nom}</div>
                          {p.representant && <div className="text-xs">{p.representant}</div>}
                          {p.adresse && <div className="text-xs text-slate-500">{p.adresse}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selected.prestations?.length > 0 && (
                  <div>
                    <div className="font-semibold flex items-center gap-1 mb-2"><Leaf className="w-4 h-4" /> Prestations & tarifs</div>
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-slate-500"><th>Type</th><th>Prix HT</th><th>Unité</th><th>Surface</th></tr></thead>
                      <tbody>
                        {selected.prestations.map((p, i) => (
                          <tr key={i} className="border-t"><td>{p.type}</td><td className="font-bold">{p.prix_ht} €</td><td>{p.unite}</td><td>{p.surface_contractuelle || '—'}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {selected.prix_vente?.length > 0 && (
                  <div>
                    <div className="font-semibold flex items-center gap-1 mb-2"><Euro className="w-4 h-4" /> Prix de vente</div>
                    {selected.prix_vente.map((pv, i) => (
                      <div key={i} className="flex justify-between py-1 border-b text-xs">
                        <span>{pv.flux}</span>
                        <span className="font-bold">{pv.montant_ht} € HT/{pv.unite}</span>
                      </div>
                    ))}
                  </div>
                )}

                {selected.clauses_particulieres && (
                  <div>
                    <div className="font-semibold mb-1">Clauses particulières</div>
                    <Textarea readOnly value={selected.clauses_particulieres} className="text-xs" rows={4} />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
